import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import BookCallModal from "../../components/feature/BookCallModal";

const services = [
  {
    number: "01",
    icon: "ri-phone-fill",
    title: "AI Lead Capture Systems",
    features: [
      "AI voice agents answer calls 24/7",
      "SMS text-back automation",
      "Website chat systems",
    ],
    outcome: "Every lead is captured instantly.",
    image: "https://public.readdy.ai/ai/img_res/0c42e256f4c49c0909f4b7dea616ff78.jpg",
  },
  {
    number: "02",
    icon: "ri-calendar-check-line",
    title: "AI Booking & Scheduling",
    features: [
      "Smart calendar automation",
      "Appointment qualification",
      "Routing logic",
    ],
    outcome: "Leads turn into scheduled revenue.",
    image: "https://readdy.ai/api/search-image?query=clean%20digital%20calendar%20scheduling%20automation%20system%20grid%20dots%20lines%20minimal%20abstract%20blue%20sapphire%20dark%20modern%20enterprise%20technology%20flow%20planning%20organized%20geometric%20clean%20background&width=600&height=400&seq=svc-booking-1&orientation=landscape",
  },
  {
    number: "03",
    icon: "ri-mail-send-line",
    title: "AI Follow-Up & Nurture",
    features: [
      "Automated SMS/email sequences",
      "Pipeline nurturing",
      "Lead reactivation",
    ],
    outcome: "No opportunity goes cold.",
    image: "https://readdy.ai/api/search-image?query=abstract%20digital%20message%20flow%20email%20automation%20sequence%20pipeline%20minimal%20blue%20sapphire%20clean%20modern%20dark%20background%20geometric%20network%20nodes%20connecting%20points%20enterprise%20technology%20data%20stream&width=600&height=400&seq=svc-followup-1&orientation=landscape",
  },
  {
    number: "04",
    icon: "ri-flow-chart",
    title: "CRM & Workflow Automation",
    features: [
      "Pipeline automation",
      "Internal workflows",
      "Centralized system management",
    ],
    outcome: "Your operations run without manual effort.",
    image: "https://readdy.ai/api/search-image?query=abstract%20crm%20workflow%20automation%20system%20diagram%20flow%20chart%20minimal%20blue%20sapphire%20dark%20background%20geometric%20enterprise%20clean%20modern%20technology%20grid%20connection%20lines%20nodes%20architecture&width=600&height=400&seq=svc-crm-1&orientation=landscape",
  },
  {
    number: "05",
    icon: "ri-line-chart-line",
    title: "Revenue Optimization Systems",
    features: [
      "Conversion tracking",
      "Funnel performance",
      "Process optimization",
    ],
    outcome: "You increase revenue without increasing workload.",
    image: "https://readdy.ai/api/search-image?query=abstract%20revenue%20growth%20funnel%20optimization%20chart%20data%20visualization%20minimal%20blue%20sapphire%20dark%20background%20geometric%20clean%20modern%20enterprise%20technology%20performance%20metric%20graph%20lines%20dashboard&width=600&height=400&seq=svc-revenue-1&orientation=landscape",
  },
];

const ServicesPage = () => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen font-sans bg-white">
      <Navbar onBookCall={() => setModalOpen(true)} />

      {/* Page Hero */}
      <section className="relative bg-charcoal pt-40 pb-24">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20geometric%20grid%20dark%20charcoal%20background%20minimal%20clean%20enterprise%20subtle%20pattern%20lines%20architecture%20blueprint%20technical%20dark%20gray%20texture&width=1440&height=400&seq=services-hero-1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="inline-block bg-sapphire/20 text-sapphire text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6">
            What We Build
          </div>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-3xl mb-5">
            AI Systems That Drive Revenue
          </h1>
          <p className="text-white/60 text-lg leading-relaxed max-w-2xl">
            We don't sell tools. We build systems that automate how your business captures, manages, and converts demand.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-8">
          {services.map((svc, i) => (
            <div
              key={svc.title}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-transform duration-300 ${
                i % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
            >
              {/* Image side */}
              <div className={`relative h-60 lg:h-auto ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <img
                  src={svc.image}
                  alt={svc.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-charcoal/30 to-transparent"></div>
              </div>

              {/* Content side */}
              <div className={`bg-light-gray p-8 lg:p-12 flex flex-col justify-center ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-xs font-bold text-sapphire/40">{svc.number}</span>
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-sapphire/10">
                    <i className={`${svc.icon} text-sapphire text-lg`}></i>
                  </div>
                </div>
                <h2 className="text-2xl lg:text-3xl font-extrabold text-charcoal mb-5">
                  {svc.title}
                </h2>
                <ul className="space-y-3 mb-6">
                  {svc.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-sapphire text-sm"></i>
                      </span>
                      <span className="text-sm text-gray-600">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-2 bg-sapphire/10 rounded-lg px-4 py-3">
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-arrow-right-circle-fill text-sapphire text-base"></i>
                  </span>
                  <span className="text-sm font-bold text-sapphire">{svc.outcome}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-deep-sapphire py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-5">Build Your System</h2>
          <p className="text-white/60 text-base max-w-lg mx-auto mb-10">
            Tell us about your operations. We'll design the exact AI system you need to capture more and convert more.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            className="bg-white hover:bg-light-gray text-deep-sapphire text-sm font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            Schedule Strategy Call
          </button>
        </div>
      </section>

      <Footer onBookCall={() => setModalOpen(true)} />
      <BookCallModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default ServicesPage;
